<template>
    <div>
        <div class="el-inline-layout">
                <div class="el-inline-side el-inline-el-status">
                    <div class="el-inline-column-label">EL Status</div>
                    <fe-remote-combo
                        label="School Year"
                        infoText="If a student was exited at any time during the year, they will be considered exited for the entire year."
                        v-model="panelValues.school_year_el_status"
                        rootProperty="years"
                        url="qry.php?action=get&property=school_years"
                        :error="schoolYearRequiredErrorElStatus"
                        :error-messages="schoolYearRequiredErrorElStatus ? 'School Year is required.' : ''"
                        data-test="el-status-school-year-filter-field"
                    />
                    <fe-remote-combo
                        label="EL Status"
                        v-model="panelValues.el_status"
                        :items="elStatusItems"
                        data-test="el-status-filter-field"
                    />
                    <div class="el-inline-toggles">
                        <v-switch v-model="panelValues.restrict" label="Restrict Results" hide-details :ripple="false" class="el-inline-switch" data-test="el-status-restrict-results-switch"/>
                    </div>
                </div>
                <div class="el-inline-divider" aria-hidden="true"/>
                <div class="el-inline-side el-inline-elp-level">
                    <div class="el-inline-column-label">ELP Level</div>
                    <fe-remote-combo
                        label="School Year"
                        infoText="If a student was exited at any time during the year, they will be considered exited for the entire year."
                        v-model="panelValues.school_year_elp_level"
                        rootProperty="years"
                        url="qry.php?action=get&property=school_years"
                        :error="schoolYearRequiredErrorElpLevel"
                        :error-messages="schoolYearRequiredErrorElpLevel ? 'School Year is required.' : ''"
                        data-test="elp-level-school-year-filter-field"
                    />
                    <fe-remote-combo
                        label="ELP Level"
                        v-model="panelValues.elp_level"
                        rootProperty="ell_level"
                        :items="elp_levels"
                        itemText="name"
                        data-test="elp-level-filter-field"
                    />
                    <div class="el-inline-toggles">
                        <v-switch v-model="panelValues.restrict_elp_level" label="Restrict Results" hide-details :ripple="false" class="el-inline-switch" data-test="elp-level-restrict-results-switch"/>
                    </div>
                </div>
            </div>
    </div>
</template>

<script>
import * as Util from '../../Util'
import FilterUtil from '../../FilterPanelUtil'

export default {
    name: 'ELPanel',

    props: {
        existingFilter: { type: Array, default: null },
    },

    rank: 1,

    data() {
        return {
            displayName: 'English Learner',
            type: 'ell',
            elItems: [{
                id: 0,
                name: 'EL Status',
                key: 'el_status',
                filterParam: 'ell_flag'
            }, {
                id: 1,
                name: 'ELP Level',
                key: 'elp_level',
                filterParam: 'ell_level_id'
            }],
            elStatusItems: [{
                id: 0,
                name: 'No'
            }, {
                id: 1,
                name: 'Yes'
            }],
            elp_levels: [],
            panelValues: {
                el_status: null,
                elp_level: null,
                require_all: false,
                restrict: true,
                restrict_elp_level: true,
                school_year_el_status: null,
                school_year_elp_level: null,
            },
        }
    },

    computed: {
        schoolYearRequiredErrorElStatus() {
            return !!this.panelValues.el_status && !this.panelValues.school_year_el_status
        },
        schoolYearRequiredErrorElpLevel() {
            return !!this.panelValues.elp_level && !this.panelValues.school_year_elp_level
        },
        schoolYearRequiredError() {
            return this.schoolYearRequiredErrorElStatus || this.schoolYearRequiredErrorElpLevel
        },
        inlineValid() {
            const elStatusOk = this.panelValues.el_status === null || !!this.panelValues.school_year_el_status
            const elpLevelOk = this.panelValues.elp_level === null || !!this.panelValues.school_year_elp_level
            return (this.panelValues.el_status !== null || this.panelValues.elp_level !== null) && elStatusOk && elpLevelOk
        },
        isDirty() {
            // Monitor form state to enable/disable apply button appropriately
            const appliedFilters = Array.isArray(this.existingFilter) ? this.existingFilter : (this.existingFilter ? [this.existingFilter] : [])
            if (!appliedFilters.length) return false
            const current = this.panelValues

            let appliedRestrict = true
            let appliedRestrictElpLevel = true
            let appliedSchoolYearElStatusId = null
            let appliedSchoolYearElpLevelId = null
            let appliedElStatus = null
            let appliedElpLevel = null
            let appliedRequireAll = false
            appliedFilters.forEach(edit => {
                const ep = edit?.editorParams
                if (!ep) return
                if (edit.type === 'el_status') {
                    if (ep.school_year) appliedSchoolYearElStatusId = ep.school_year.id
                    appliedRestrict = ep.restrict
                    appliedElStatus = ep.el_status
                } else if (edit.type === 'elp_level') {
                    if (ep.school_year) appliedSchoolYearElpLevelId = ep.school_year.id
                    appliedRestrictElpLevel = ep.restrict
                    appliedElpLevel = ep.elp_level?.[0] ?? null
                    appliedRequireAll = ep.require_all
                }
            })

            if (appliedRestrict !== current.restrict || appliedRestrictElpLevel !== current.restrict_elp_level) return true

            const currentElStatusYearId = current.school_year_el_status?.id ?? null
            const currentElpLevelYearId = current.school_year_elp_level?.id ?? null
            if ((appliedSchoolYearElStatusId ?? null) !== currentElStatusYearId || (appliedSchoolYearElpLevelId ?? null) !== currentElpLevelYearId) return true

            if (appliedRequireAll !== current.require_all) return true

            const appliedElStatusId = appliedElStatus?.id ?? null
            const currentElStatusId = current.el_status?.id ?? null
            if (appliedElStatusId !== currentElStatusId) return true

            const appliedElpLevelId = appliedElpLevel?.id ?? null
            const currentElpLevelId = current.elp_level?.id ?? null
            return appliedElpLevelId !== currentElpLevelId
        },
    },

    watch: {
        existingFilter: {
            handler(f) {
                // Sync form state from the applied EL filter(s) when they change; clear form when the filter is removed.
                if (f) {
                    const list = Array.isArray(f) ? f : [f]
                    if (list.length) {
                        let restrict = true
                        let school_year_el_status = null
                        let school_year_elp_level = null
                        let el_status = null
                        let elp_level = null
                        let require_all = false
                        let restrict_elp_level = true
                        list.forEach(edit => {
                            const ep = edit?.editorParams
                            if (!ep) return
                            if (edit.type === 'el_status') {
                                if (ep.school_year) school_year_el_status = { ...ep.school_year }
                                restrict = ep.restrict
                                el_status = ep.el_status
                            } else if (edit.type === 'elp_level') {
                                if (ep.school_year) school_year_elp_level = { ...ep.school_year }
                                restrict_elp_level = ep.restrict
                                elp_level = ep.elp_level?.[0] ?? null
                                require_all = ep.require_all
                            }
                        })
                        this.panelValues = {
                            restrict,
                            restrict_elp_level,
                            school_year_el_status,
                            school_year_elp_level,
                            el_status,
                            elp_level,
                            require_all,
                        }
                    }
                } else {
                    this.panelValues = {
                        el_status: null,
                        elp_level: null,
                        require_all: false,
                        restrict: true,
                        restrict_elp_level: true,
                        school_year_el_status: null,
                        school_year_elp_level: null,
                    }
                    this.$nextTick(() => this.loadDefaultSchoolYear())
                }
            },
            immediate: true,
        },
        inlineValid: {
            handler(v) {
                this.$emit('valid-change', v)
            },
            immediate: true,
        },
        schoolYearRequiredError: {
            handler(v) {
                this.$emit('invalid-change', v)
            },
            immediate: true,
        },
        isDirty: {
            handler(v) {
                this.$emit('dirty-change', v)
            },
            immediate: true,
        },
    },

    mounted() {
        this.loadDefaultSchoolYear()
        this.getEllLevels()
        this.$emit('valid-change', this.inlineValid)
    },

    methods: {
        loadDefaultSchoolYear() {
            this.$axios.get('qry.php?action=get&property=school_years').then((r) => {
                const years = r.data?.years || []
                if (years.length) {
                    if (!this.panelValues.school_year_el_status) this.$set(this.panelValues, 'school_year_el_status', years[0])
                    if (!this.panelValues.school_year_elp_level) this.$set(this.panelValues, 'school_year_elp_level', years[0])
                }
            })
        },
        getFilter() {
            const filters = []
            const syElStatus = this.panelValues.school_year_el_status
            const syElpLevel = this.panelValues.school_year_elp_level
            if (!syElStatus && !syElpLevel) return filters.length ? filters : null
            const elStatusItem = this.$_.find(this.elItems, z => z.key === 'el_status')
            const elpLevelItem = this.$_.find(this.elItems, z => z.key === 'elp_level')
            if (this.panelValues.el_status !== null && syElStatus) {
                const f = this._buildOneFilter(elStatusItem, this.panelValues.el_status, null, syElStatus)
                if (f) filters.push(f)
            }
            if (this.panelValues.elp_level !== null && syElpLevel) {
                const f = this._buildOneFilter(elpLevelItem, null, this.panelValues.elp_level, syElpLevel)
                if (f) filters.push(f)
            }
            return filters.length ? filters : null
        },

        _buildOneFilter(selection, elStatusVal, elpLevelVal, schoolYear) {
            if (!selection || !schoolYear) return null
            const filterParam = selection.filterParam
            const key = selection.key
            const name = selection.name
            const isElpLevel = key === 'elp_level'
            const restrict = isElpLevel ? this.panelValues.restrict_elp_level : this.panelValues.restrict
            const require_all = isElpLevel ? this.panelValues.require_all : false
            const elp_level = elpLevelVal !== null ? [elpLevelVal] : []
            const ids = filterParam === 'ell_level_id'
                ? FilterUtil.collectIdsFromSelection(elp_level, { idKey: 'id', parseInteger: true })
                : ((elStatusVal?.id ?? null) !== null ? [elStatusVal.id] : [])
            if (!ids.length) return null
            const uniqueId = Util.UUIDv4()
            const params = this.$_.pickBy({
                [filterParam]: ids,
                require_all: isElpLevel ? require_all : undefined,
                school_year_id: schoolYear?.id,
            }, x => !this.$_.isBlank(x))
            const filter = {
                type: key,
                id: uniqueId,
                restrict,
                report: true,
                params,
            }
            const editorParams = {
                restrict,
                el_status: this.panelValues.el_status,
                elp_level: this.panelValues.elp_level !== null ? [this.panelValues.elp_level] : [],
                require_all: this.panelValues.require_all,
                school_year: schoolYear ? { ...schoolYear } : null,
            }
            const tooltipRestrict = isElpLevel ? this.panelValues.restrict_elp_level : this.panelValues.restrict
            const tooltipRequireAll = isElpLevel ? this.panelValues.require_all : undefined
            const valueLabel = filterParam === 'ell_flag' ? (elStatusVal?.name || '') : (elpLevelVal?.name || '')
            FilterUtil.attachFilterDescriptor(filter, {
                editor: this,
                editorParams,
                chipText: name,
                tooltipData: this.$_.pickBy({
                    [name]: valueLabel,
                    'Restrict': tooltipRestrict,
                    'Require All': tooltipRequireAll,
                    'School Year': schoolYear?.name,
                }, x => !this.$_.isBlank(x)),
                grouper: {
                    id: uniqueId,
                    // Includes the value so the tab doesn't collide with the standard EL Status / ELP Level tabs
                    name: valueLabel ? `${name}: ${valueLabel}` : name,
                    type: 'propertyArray',
                    args: { path: `filters.${uniqueId}`, property: 'value' },
                },
            })
            return filter
        },

        getEllLevels() {
            this.$modelGet('ellLevel')
                .then((res) => {
                    // $modelGet resolves with the rootProperty value (the array), not response.data
                    const data = Array.isArray(res) ? res : (res?.data?.ell_level ?? res?.ell_level ?? [])
                    const filtered = []
                    data.forEach((itm) => {
                        if (!itm.display_name_group) {
                            itm.name = itm.display_name || itm.name
                            filtered.push(itm)
                        } else {
                            // display_name_group values are strings from API; coerce for strict comparison
                            const o = data.find(x => Number(x.id) === Number(itm.display_name_group[0]))
                            if (!filtered.find(x => x.display_name === o.display_name)) {
                                filtered.push({ ...o, name: o.display_name || o.name })
                            }
                        }
                    })
                    this.elp_levels = filtered
                })
        }
    }
}
</script>

<style lang="scss" scoped>
.el-inline-layout {
    display: flex;
    flex-direction: row;
    align-items: stretch;
    width: 100%;
    min-width: 0;
}

.el-inline-side {
    display: flex;
    flex-direction: column;
    gap: 12px 0;
    flex: 1 1 0;
    min-width: 0;
}

.el-inline-divider {
    width: 1px;
    flex: 0 0 1px;
    background-color: #E0E1E6;
    margin: 0 16px;
    align-self: stretch;
}

.el-inline-column-label {
    font-size: 12px;
    color: rgba(0, 0, 0, 0.6);
    margin-bottom: 4px;
}

.el-inline-toggles {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: center;
    gap: 0 16px;
    margin-top: 4px;
}

.el-inline-switch {
    flex: 0 0 auto;
}
</style>
